import { Component, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { DateTimeService } from '../../services/date-time.service';

@Component({
	selector: 'app-parcel-items',
	templateUrl: './parcel-items.component.html',
	styleUrls: ['./parcel-items.component.scss']
})

export class ParcelItemsComponent implements OnInit {
	public dataSource = new MatTableDataSource<any>();
	public dateTimeFormat: any;
	public displayedColumns: string[] = ['parcelItem', 'quantity', 'barcode', 'scannedWhen', 'comments', 'refrigerationType'];
	public page = 1;
	@ViewChild(MatPaginator) public paginator: MatPaginator;
	public itemFilter = 'showAll';
	@Input() public assignments: any[] = [];
	@Input() public stopType: string;
	public assignmentIdNamePairs: any[] = [];

	constructor(private _dateTimeService: DateTimeService) {
	}

	calcTotalItems() {
		let total = 0;
		(this.dataSource.data ?? []).forEach((item) => {
			total += item?.quantity ?? 0;
		});
		return total;
	}

	filterItems(value) {
		this.itemFilter = value;
		if (value == 'showAll') {
			this.dataSource.data = this.getAllItems();
		} else if (value == null) {
			// A nullish selection must match nothing. Both branches below compare against a nullable
			// assignment field, so undefined == undefined would otherwise select a null-typed or id-less
			// assignment and show its items as though that leg had been picked.
			this.dataSource.data = [];
		} else if (this.stopType == 'ONDEMAND') {
			// assignment.type is nullable on the wire, so it needs the safe call before .toLowerCase().
			const wanted = value.toLowerCase();
			this.dataSource.data = this.itemsOf(
				(this.assignments ?? []).find(a => a?.type?.toLowerCase() === wanted));
		} else if (this.stopType == 'ROUTESTOP') {
			this.dataSource.data = this.itemsOf((this.assignments ?? []).find(a => a?.id == value));
		}
	}

	getAllItems() {
		return (this.assignments ?? []).map(x => this.itemsOf(x)).flat(1);
	}

	// items may be null on the wire; always return a real array. Array.flat() only unwraps elements that ARE
	// arrays, so a null left in place here counts towards length and renders as an all-blank row.
	private itemsOf(assignment: { items?: any[] } | null | undefined): any[] {
		return (assignment?.items ?? []).filter(item => item != null);
	}

	ngAfterViewInit(): void {
		this.dataSource.paginator = this.paginator;
	}

	ngOnChanges(changes: SimpleChanges) {
		// The pairs are a derived view of assignments, so they have to be rebuilt whenever it changes —
		// ngOnInit alone leaves the ROUTESTOP dropdown empty for a host that supplies its data late.
		if (changes.assignments) {
			this.assignmentIdNamePairs = this.buildAssignmentIdNamePairs();
		}
		this.filterItems(this.itemFilter);
	}

	ngOnInit(): void {
		this.dateTimeFormat = this._dateTimeService.getDateTime();
		this.assignmentIdNamePairs = this.buildAssignmentIdNamePairs();
	}

	// Nameless assignments sort last: a stop has no name when it did not resolve, or when the backend
	// withheld it as another customer's. The option still has to exist because it filters by id, and the
	// template labels it rather than rendering it blank.
	private buildAssignmentIdNamePairs(): any[] {
		// The comparator takes both arguments deliberately: a one-argument form returns a string, and a
		// comparator result that is not a number is treated as "equal", which leaves payload order.
		return (this.assignments ?? [])
			.map(x => ({ id: x?.id, name: x?.address?.name }))
			.sort((a, b) => {
				if (!a.name && !b.name) { return 0; }
				if (!a.name) { return 1; }
				if (!b.name) { return -1; }
				return a.name.localeCompare(b.name);
			});
	}
}
